import React, { useMemo, useState } from 'react';
import {
  ActionIcon,
  Alert,
  Button,
  Center,
  Container,
  Group,
  Loader,
  SegmentedControl,
  Stack,
  Title,
} from '@mantine/core';
import { IconChevronLeft, IconChevronRight } from '@tabler/icons-react';
import dayjs from 'dayjs';
import { useTranslation } from 'react-i18next';
import { AgendaList } from '../../components/calendar/AgendaList';
import { MonthGrid } from '../../components/calendar/MonthGrid';
import { SourceLegend } from '../../components/calendar/SourceLegend';
import { SubscribeModal } from '../../components/calendar/SubscribeModal';
import { calendarRange } from './calendarRange';
import '../../components/calendar/calendar.css';
import { CalendarScope, useCalendar, useCalendarContext } from '../../hooks/useCalendarApi';
import { useDevice } from '../../hooks/useDevice';

export const CalendarPage: React.FC = () => {
  const { t } = useTranslation();
  const device = useDevice();
  const context = useCalendarContext();

  const [cursor, setCursor] = useState(() => dayjs().startOf('month'));
  // Куда листают: сетка уезжает в ту же сторону. Без направления переход
  // выглядит одинаково и вперёд, и назад — то есть ничего не сообщает.
  const [isGoingBack, setIsGoingBack] = useState(false);
  // Выбранный в сетке день: «ещё N» ведёт в список именно этого дня, а не в
  // общий список на шесть недель.
  const [pickedDay, setPickedDay] = useState<string | null>(null);
  // null — человек ещё не выбирал вид: useDevice сначала отдаёт 'desktop',
  // поэтому решение не замораживаем в useState.
  const [chosenView, setView] = useState<'month' | 'list' | null>(null);
  const view = chosenView ?? (device === 'mobile' ? 'list' : 'month');
  const [scope, setScope] = useState<CalendarScope>('me');
  const [isSubscribeOpen, setIsSubscribeOpen] = useState(false);

  const timeZone = context.data?.timeZone ?? 'Europe/Moscow';
  const hasTeamScope = context.data?.hasTeamScope === true;
  // Права считает сервер (спека прав): владелец без флага admin тоже видит всю организацию.
  const hasCompanyScope = context.data?.hasCompanyScope === true;

  const { from, to } = useMemo(() => calendarRange(cursor), [cursor]);
  const { data = [], isLoading, isError } = useCalendar(from, to, scope);

  const scopes = [{ value: 'me', label: t('calendar.page.scope.me') }];
  if (hasTeamScope) scopes.push({ value: 'team', label: t('calendar.page.scope.team') });
  if (hasCompanyScope) scopes.push({ value: 'tenant', label: t('calendar.page.scope.tenant') });

  return (
    <Container size="lg" py="md">
      <Stack>
        <Group justify="space-between" wrap="wrap">
          <Group gap="xs">
            <ActionIcon
              variant="subtle"
              aria-label={t('calendar.page.prev')}
              onClick={() => {
                setIsGoingBack(true);
                setPickedDay(null);
                setCursor(cursor.subtract(1, 'month'));
              }}
            >
              <IconChevronLeft size={18} />
            </ActionIcon>
            <Title order={3}>{cursor.format('MMMM YYYY')}</Title>
            <ActionIcon
              variant="subtle"
              aria-label={t('calendar.page.next')}
              onClick={() => {
                setIsGoingBack(false);
                setPickedDay(null);
                setCursor(cursor.add(1, 'month'));
              }}
            >
              <IconChevronRight size={18} />
            </ActionIcon>
            <Button
              variant="light"
              size="xs"
              onClick={() => {
                setIsGoingBack(cursor.isAfter(dayjs(), 'month'));
                setPickedDay(null);
                setCursor(dayjs().startOf('month'));
              }}
            >
              {t('calendar.page.today')}
            </Button>
          </Group>

          <Group gap="xs">
            <SegmentedControl
              size="xs"
              value={view}
              onChange={(value) => setView(value as 'month' | 'list')}
              data={[
                { value: 'month', label: t('calendar.page.month') },
                { value: 'list', label: t('calendar.page.list') },
              ]}
            />
            {scopes.length > 1 && (
              <SegmentedControl
                size="xs"
                value={scope}
                onChange={(value) => setScope(value as CalendarScope)}
                data={scopes}
              />
            )}
            <Button variant="default" size="xs" onClick={() => setIsSubscribeOpen(true)}>
              {t('calendar.page.subscribe')}
            </Button>
          </Group>
        </Group>

        {isError ? (
          <Alert color="red">{t('calendar.page.loadFailed')}</Alert>
        ) : isLoading ? (
          <Center py="xl">
            <Loader />
          </Center>
        ) : view === 'month' ? (
          <div
            key={cursor.format('YYYY-MM')}
            className={`calendar-slide${isGoingBack ? ' calendar-slide--back' : ''}`}
          >
            <MonthGrid
              year={cursor.year()}
              month={cursor.month() + 1}
              items={data}
              timeZone={timeZone}
              showPerson={scope !== 'me'}
              onMore={(date) => {
                setPickedDay(date);
                setView('list');
              }}
            />
          </div>
        ) : (
          <AgendaList
            items={data}
            timeZone={timeZone}
            showPerson={scope !== 'me'}
            onlyDay={pickedDay}
            onClearDay={() => setPickedDay(null)}
          />
        )}

        {!isLoading && !isError && <SourceLegend items={data} />}
      </Stack>

      <SubscribeModal opened={isSubscribeOpen} onClose={() => setIsSubscribeOpen(false)} />
    </Container>
  );
};
